import React, { useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { messaggioErrore } from "../utils/erroriUtente";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useFeatures, PLANS, FEATURE_DEFINITIONS, PLAN_ORDER } from "../contexts/FeaturesContext";
import { useToast } from "../utils/Toast";
import { CheckCircle, Lock, Zap, ChevronRight } from "lucide-react";

export default function FeaturesSettingsPage() {
  const { isPlatformOwner, isOrgAdmin } = useAuth();
  // organization da useOrg() (orgVisitata), non da useAuth() — bug reale corretto oggi: usare
  // quella del profilo personale avrebbe rischiato di scrivere sull'organizzazione propria del
  // Platform Owner invece di quella che si intende davvero modificare, in caso di "Entra".
  const { orgVisitata: organization } = useOrg();
  const { hasFeature, orgPlan } = useFeatures();
  const { addToast } = useToast();
  const [saving, setSaving] = useState(null);

  if (!isOrgAdmin && !isPlatformOwner) {
    return <div style={{padding:40,textAlign:"center",color:"#9ca3af"}}>Accesso non autorizzato</div>;
  }

  const currentPlan = PLANS[orgPlan] || PLANS.free;
  const currentPlanOrder = PLAN_ORDER[orgPlan] || 0;

  const toggleFeature = async (featureKey, currentValue) => {
    const def = FEATURE_DEFINITIONS[featureKey];

    // Verifica che il piano copra questa feature
    if (!isPlatformOwner && PLAN_ORDER[def.plan] > currentPlanOrder) {
      addToast(`Questa funzionalità richiede il piano ${PLANS[def.plan]?.label}`, "error");
      return;
    }

    setSaving(featureKey);
    try {
      await updateDoc(doc(db,"organizations",organization.id), {
        [`features.${featureKey}`]: !currentValue
      });
      addToast(`Funzionalità ${!currentValue ? "attivata" : "disattivata"}!`, "success");
    } catch(err) {
      addToast(messaggioErrore(err), "error");
    }
    setSaving(null);
  };

  const features = Object.entries(FEATURE_DEFINITIONS);
  const orgFeatures = organization?.features || {};

  return (
    <div style={{maxWidth:700, padding:24, flex:1, overflowY:"auto"}}>
      <h2 style={{fontSize:18,fontWeight:700,marginBottom:4}}>⚡ Funzionalità</h2>
      <p style={{fontSize:13,color:"#6b7280",marginBottom:24}}>
        Attiva o disattiva le funzionalità disponibili per il tuo piano.
      </p>

      {/* Piano attuale */}
      <div style={{background:`${currentPlan.color}11`,border:`1px solid ${currentPlan.color}44`,borderRadius:12,padding:"14px 18px",marginBottom:24,display:"flex",alignItems:"center",gap:12}}>
        <div style={{width:40,height:40,borderRadius:10,background:currentPlan.color,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <Zap size={20} color="#fff"/>
        </div>
        <div style={{flex:1}}>
          <div style={{fontSize:14,fontWeight:700,color:currentPlan.color}}>Piano {currentPlan.label}</div>
          <div style={{fontSize:12,color:"#6b7280"}}>{currentPlan.price}</div>
        </div>
        {!isPlatformOwner && orgPlan !== "enterprise" && (
          <button style={{padding:"6px 14px",background:currentPlan.color,color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            Aggiorna piano
          </button>
        )}
      </div>

      {/* Lista feature */}
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {features.map(([key, def]) => {
          const requiredPlanOrder = PLAN_ORDER[def.plan] || 0;
          const isPlanSufficient = isPlatformOwner || currentPlanOrder >= requiredPlanOrder;
          const isActive = hasFeature(key);
          const isFreeFeature = def.plan === "free";
          const isLoading = saving === key;
          const requiredPlan = PLANS[def.plan];

          return (
            <div key={key} style={{
              display:"flex", alignItems:"center", gap:14,
              padding:"14px 18px",
              background: isActive ? "#fafafa" : "#fff",
              border: `1px solid ${isActive ? "#e5e7eb" : "#f3f4f6"}`,
              borderRadius:10,
              opacity: isPlanSufficient ? 1 : 0.7,
            }}>
              {/* Toggle */}
              <div onClick={() => !isFreeFeature && isPlanSufficient && toggleFeature(key, isActive)}
                style={{
                  width:44, height:24, borderRadius:12,
                  background: isActive ? "#7c3aed" : "#e5e7eb",
                  position:"relative", cursor: isFreeFeature || !isPlanSufficient ? "not-allowed" : "pointer",
                  transition:"background 0.2s", flexShrink:0,
                }}>
                <div style={{
                  width:18, height:18, borderRadius:"50%", background:"#fff",
                  position:"absolute", top:3,
                  left: isActive ? 23 : 3,
                  transition:"left 0.2s",
                  boxShadow:"0 1px 3px rgba(0,0,0,0.2)"
                }}/>
              </div>

              {/* Info */}
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:14,fontWeight:600,color:"#111827"}}>{def.label}</span>
                  {/* Badge piano richiesto */}
                  <span style={{fontSize:10,fontWeight:700,padding:"1px 6px",borderRadius:10,
                    background: requiredPlan.color+"22", color: requiredPlan.color}}>
                    {requiredPlan.label.toUpperCase()}
                  </span>
                  {isFreeFeature && (
                    <span style={{fontSize:10,color:"#9ca3af",fontStyle:"italic"}}>sempre attiva</span>
                  )}
                </div>
                <div style={{fontSize:12,color:"#9ca3af",marginTop:2}}>{def.description}</div>
              </div>

              {/* Stato */}
              {!isPlanSufficient ? (
                <div style={{display:"flex",alignItems:"center",gap:4,color:"#9ca3af",fontSize:12}}>
                  <Lock size={13}/>
                  <span>Richiede {requiredPlan.label}</span>
                </div>
              ) : isLoading ? (
                <span style={{fontSize:12,color:"#9ca3af"}}>...</span>
              ) : isActive ? (
                <CheckCircle size={16} color="#059669"/>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* Piano Enterprise promo */}
      {orgPlan !== "enterprise" && (
        <div style={{marginTop:20,background:"linear-gradient(135deg,#059669,#0891b2)",borderRadius:12,padding:"18px 20px",color:"#fff"}}>
          <div style={{fontSize:14,fontWeight:700,marginBottom:4}}>🚀 Piano Enterprise</div>
          <div style={{fontSize:12,opacity:0.9,marginBottom:12}}>
            Report avanzati, accesso API, supporto dedicato e funzionalità future in anteprima.
          </div>
          <button style={{padding:"6px 16px",background:"rgba(255,255,255,0.2)",color:"#fff",border:"1px solid rgba(255,255,255,0.4)",borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",gap:6}}>
            Contatta il supporto <ChevronRight size={13}/>
          </button>
        </div>
      )}
    </div>
  );
}
